import { useUnderlyingRoundPhases } from "@client/hooks/use-round-phase";
import {
  useImpersonateContext,
  useRoundAdmin,
} from "@client/ImpersonateContext";
import { roundUsesTracks } from "@engine/round/accessors";
import {
  adminNavItemIsActive,
  buildRoundAdminOrFunderNavItems,
  isRoundAdminNavItemVisible,
} from "@client/utils/roundAdminNav";
import { Button, cn } from "@sfc/ui";
import { PanelLeftIcon, PanelLeftCloseIcon } from "lucide-react";
import { useState, type ReactNode } from "react";
import { RouteScroller } from "@client/components/utils/RouteScroller";
import { Link, useLocation } from "wouter";

export function RoundAdminNavbar({
  roundId,
  children,
}: {
  roundId: string;
  children: ReactNode;
}) {
  const [pathname] = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const currentPhases = useUnderlyingRoundPhases();
  const { isAdmin, isFunder } = useRoundAdmin();
  const { value } = useImpersonateContext();

  const visibleItems = buildRoundAdminOrFunderNavItems(
    roundId,
    true,
    value?.round.isSfcRound ?? true,
    value ? roundUsesTracks(value.round) : true,
  ).filter((item) =>
    isRoundAdminNavItemVisible(item, {
      isAdmin,
      isFunder,
      currentPhases,
    }),
  );

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="lg:hidden overflow-x-auto border-b shrink-0">
        <div className="flex items-center gap-2 px-4 py-3">
          {visibleItems.map(({ label, href, icon: Icon }) => {
            const isActive = adminNavItemIsActive(
              pathname,
              roundId,
              href,
              true,
            );
            return (
              <Link
                key={href}
                href={href}
                aria-current={isActive ? "page" : undefined}
                className={cn(
                  "flex items-center gap-2 h-9 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors",
                  isActive
                    ? "bg-surface-elevated text-text-primary border border-border-subtle shadow-sm font-medium"
                    : "text-text-primary hover:bg-surface-hover bg-surface-base border border-border-subtle",
                )}
              >
                <Icon className="h-4 w-4" />
                {label}
              </Link>
            );
          })}
        </div>
      </div>
      <div className="flex-1 flex overflow-hidden">
        <aside
          aria-label="Round administration"
          className={cn(
            "hidden lg:flex flex-col shrink-0 overflow-y-auto",
            isCollapsed ? "w-14" : "w-60",
          )}
        >
          <div
            className={cn(
              "flex pt-3 pb-2",
              isCollapsed ? "justify-center px-1" : "justify-end px-3",
            )}
          >
            <Button
              variant="ghost"
              size="icon"
              className="pointer-coarse:size-11"
              title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-expanded={!isCollapsed}
              onClick={() => setIsCollapsed((collapsed) => !collapsed)}
            >
              {isCollapsed ? <PanelLeftIcon /> : <PanelLeftCloseIcon />}
            </Button>
          </div>
          <nav
            className={cn(
              "space-y-0.5",
              isCollapsed ? "px-1 pb-3" : "px-3 pb-3",
            )}
          >
            {visibleItems.map(
              ({ label, href, icon: Icon, separatorBefore }) => {
                const isActive = adminNavItemIsActive(
                  pathname,
                  roundId,
                  href,
                  true,
                );
                return (
                  <div key={href}>
                    {separatorBefore && (
                      <hr className="my-2 border-border-subtle" />
                    )}
                    <Link
                      href={href}
                      aria-label={label}
                      aria-current={isActive ? "page" : undefined}
                      title={isCollapsed ? label : undefined}
                      className={cn(
                        "flex min-h-11 items-center gap-3 py-2.5 rounded-lg text-sm transition-colors",
                        isCollapsed ? "justify-center px-0" : "px-3",
                        isActive
                          ? "bg-surface-elevated text-text-primary border border-border-subtle shadow-sm"
                          : "text-text-secondary hover:text-text-primary hover:bg-surface-hover",
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      {!isCollapsed && <span>{label}</span>}
                    </Link>
                  </div>
                );
              },
            )}
          </nav>
        </aside>
        <RouteScroller>{children}</RouteScroller>
      </div>
    </div>
  );
}
